type MaybeText = string | null | undefined;

interface QuoteHeaderLike {
  angebot_nr?: MaybeText;
  kunde?: MaybeText;
  bauvorhaben?: MaybeText;
}

interface QuoteMetadataLike {
  offerNumber?: MaybeText;
  customerName?: MaybeText;
  projectName?: MaybeText;
  slot?: number | null;
}

interface DatabaseProjectInfoLike {
  customerName?: MaybeText;
  projectNumber?: MaybeText;
  description?: MaybeText;
  location?: MaybeText;
}

interface GAEBMetadataLike {
  projectName?: MaybeText;
  clientName?: MaybeText;
  originalFilename?: MaybeText;
}

interface SourceInfoLike {
  originalFilename?: MaybeText;
}

export interface QuoteNamingInput extends QuoteMetadataLike {
  header?: QuoteHeaderLike | null;
  metadata?: QuoteMetadataLike | null;
}

export interface DatabaseNamingInput {
  name?: MaybeText;
  projectInfo?: DatabaseProjectInfoLike | null;
  gaebMetadata?: GAEBMetadataLike | null;
  source?: SourceInfoLike | null;
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function pickText(...values: unknown[]): string | undefined {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
}

function resolveHeader(input: QuoteNamingInput): QuoteHeaderLike {
  return (input.header ?? input) as QuoteHeaderLike;
}

function resolveMetadata(input: QuoteNamingInput): QuoteMetadataLike {
  return (input.metadata ?? input) as QuoteMetadataLike;
}

export function getQuoteOfferNumber(input: QuoteNamingInput): string | undefined {
  const header = resolveHeader(input);
  const metadata = resolveMetadata(input);
  return pickText(metadata.offerNumber, header.angebot_nr);
}

export function getQuoteCustomerName(input: QuoteNamingInput): string | undefined {
  const header = resolveHeader(input);
  const metadata = resolveMetadata(input);
  return pickText(metadata.customerName, header.kunde);
}

export function getQuoteProjectName(input: QuoteNamingInput): string | undefined {
  const header = resolveHeader(input);
  const metadata = resolveMetadata(input);
  return pickText(metadata.projectName, header.bauvorhaben);
}

export function getQuoteDisplayName(input: QuoteNamingInput, fallback = 'Angebot'): string {
  return (
    getQuoteProjectName(input) ||
    getQuoteOfferNumber(input) ||
    getQuoteCustomerName(input) ||
    fallback
  );
}

export interface QuoteListPresentation {
  /** Anzeigetitel — bestehende Titel-Logik (Bauvorhaben > Nummer > Kunde > Fallback) bleibt unverändert. */
  title: string;
  /**
   * Angebotsnummer für ein eigenes, sichtbares Element in Karten-/Listenansicht.
   * Bewusst IMMER gesetzt, wenn eine Nummer existiert — auch wenn sie dem Titel
   * gleicht. (Vorher verschwand die Nummer bei Titelgleichheit aus der Metazeile;
   * Olli fand sie deshalb nirgends.)
   */
  offerNumber?: string;
  /** Graue Zusatzzeile (Kunde) — OHNE die Nummer, die steht jetzt separat. */
  metaParts: string[];
}

export function getQuoteListPresentation(
  input: QuoteNamingInput,
  fallback = 'Angebot',
): QuoteListPresentation {
  const title = getQuoteDisplayName(input, fallback);
  const offerNumber = getQuoteOfferNumber(input);
  const customerName = getQuoteCustomerName(input);

  const metaParts: string[] = [];
  if (
    customerName &&
    customerName !== 'Auto-Save' &&
    customerName !== 'Arbeitsfortschritt' &&
    customerName !== title
  ) {
    metaParts.push(customerName);
  }

  return { title, offerNumber, metaParts };
}

export function getQuoteFileStem(input: QuoteNamingInput, fallbackTitle = 'Angebot'): string {
  const offerNumber = getQuoteOfferNumber(input);
  const title = getQuoteProjectName(input) || getQuoteCustomerName(input) || fallbackTitle;

  if (offerNumber && title && offerNumber !== title) {
    return `${offerNumber} - ${title}`;
  }

  return offerNumber || title || fallbackTitle;
}

export function sanitizeFilenamePart(value: string, fallback = 'Datei'): string {
  const safe = value
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return safe || fallback;
}

export function getDatabaseProjectName(input: DatabaseNamingInput): string | undefined {
  return pickText(
    input.gaebMetadata?.projectName,
    input.projectInfo?.description,
    input.projectInfo?.projectNumber,
    input.name,
  );
}

export function getDatabaseCustomerName(input: DatabaseNamingInput): string | undefined {
  return pickText(
    input.gaebMetadata?.clientName,
    input.projectInfo?.customerName,
  );
}

export function getDatabaseSourceName(input: DatabaseNamingInput): string | undefined {
  return pickText(
    input.gaebMetadata?.originalFilename,
    input.source?.originalFilename,
    input.name,
  );
}

export function getGaebExportBaseName(input: DatabaseNamingInput, fallback = 'Projekt'): string {
  return getDatabaseProjectName(input) || getDatabaseSourceName(input) || fallback;
}
